// Клиент для `/api/public/drillstrings` — компоновки инструмента.
// Типы — единый источник правды: ts-rs-генерация из
// `crates/backend/web_api_types/src/drillstring.rs` (alias @webtypes, см.
// vite.config.ts / tsconfig.app.json). Регенерация — `cargo test -p web_api_types`.
//
// ShareDto / CreateShareBody / ReplaceSectionsBody остаются локальными:
// у генерации нет точно-именованных аналогов (есть ShareWithMeta /
// ShareTokenPayloadDto — другая форма, не маппим наугад).
//
// DrillstringDetailDto тоже остаётся ЛОКАЛЬНЫМ (намеренно): генерёный
// вариант несёт `tools_resolved: ToolDto[]` на ГЕНЕРЁНОМ ToolDto, чей
// `kind` — генерёный ToolKind (17 вариантов, +dv_collar/packer). Локальный
// ToolDto (`./tool`) на локальном 15-вариантном ToolKind — пока они
// разъезжаются (см. api/tool.ts), генерёный DetailDto не присваивается к
// `Record<string, ToolDto>` в DrillstringPage. Мигрировать вместе с tool.ts
// (после синка `../tool_kind`).

import type { DrillstringSummaryDto } from '@webtypes/DrillstringSummaryDto'
import type { DrillstringSectionDto } from '@webtypes/DrillstringSectionDto'
import type { DrillstringSectionInput } from '@webtypes/DrillstringSectionInput'
import type { CreateDrillstringBody } from '@webtypes/CreateDrillstringBody'
import type { UpdateDrillstringHeaderBody } from '@webtypes/UpdateDrillstringHeaderBody'
import type { ToolDto } from './tool'

export type {
  DrillstringSummaryDto,
  DrillstringSectionDto,
  DrillstringSectionInput,
  CreateDrillstringBody,
  UpdateDrillstringHeaderBody,
}

export type DrillstringDetailDto = {
  id: number
  name: string
  description: string
  created_at: string
  updated_at: string
  sections: DrillstringSectionDto[]
  is_owner: boolean
  owner_email: string | null
  owner_name: string | null
  // Все tool-записи, на которые ссылаются sections. Для shared-recipient'а
  // это единственный способ получить данные о чужом tool'е (его обычный
  // GET /tool отфильтрует чужие). Owner получает то же поле дублирующе.
  tools_resolved: ToolDto[]
}

export type ShareDto = {
  // user.id получателя — стабильный идентификатор grant'а. Раньше тут был
  // public_user.id; переезд: бэкенд m20260513_000001 (drillstring_share переехал
  // на user_id, public_user остался только OAuth-нашлёпкой).
  granted_to_user_id: number
  // email/name резолвятся через мост user → public_user.linked_user_id →
  // oauth_identity, поэтому могут быть null если у получателя нет OAuth-входа.
  email: string | null
  name: string | null
  created_at: string
}

export type CreateShareBody = {
  email: string
}

export type ReplaceSectionsBody = {
  sections: DrillstringSectionInput[]
}

const BASE = '/api/public/drillstrings'

async function ensureOk(resp: Response): Promise<void> {
  if (!resp.ok) {
    const text = await resp.text().catch(() => '')
    throw new Error(`${resp.status} ${resp.statusText}${text ? ': ' + text : ''}`)
  }
}

export async function listDrillstrings(): Promise<DrillstringSummaryDto[]> {
  const resp = await fetch(BASE, { credentials: 'same-origin' })
  await ensureOk(resp)
  return resp.json()
}

export async function getDrillstring(id: number): Promise<DrillstringDetailDto> {
  const resp = await fetch(`${BASE}/${id}`, { credentials: 'same-origin' })
  await ensureOk(resp)
  return resp.json()
}

export async function createDrillstring(body: CreateDrillstringBody): Promise<DrillstringDetailDto> {
  const resp = await fetch(BASE, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  await ensureOk(resp)
  return resp.json()
}

export async function updateDrillstringHeader(
  id: number,
  body: UpdateDrillstringHeaderBody,
): Promise<DrillstringSummaryDto> {
  const resp = await fetch(`${BASE}/${id}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  await ensureOk(resp)
  return resp.json()
}

export async function deleteDrillstring(id: number): Promise<void> {
  const resp = await fetch(`${BASE}/${id}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  })
  await ensureOk(resp)
}

export async function replaceSections(id: number, body: ReplaceSectionsBody): Promise<DrillstringDetailDto> {
  const resp = await fetch(`${BASE}/${id}/sections`, {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  await ensureOk(resp)
  return resp.json()
}

// ─── Sharing ──────────────────────────────────────────────────────────

export async function listShares(drillstringId: number): Promise<ShareDto[]> {
  const resp = await fetch(`${BASE}/${drillstringId}/shares`, { credentials: 'same-origin' })
  await ensureOk(resp)
  return resp.json()
}

// 400 — пустой email или попытка расшарить самому себе
// 404 — компоновка чужая, либо нет public_user'а с таким email
export async function createShare(drillstringId: number, email: string): Promise<void> {
  const resp = await fetch(`${BASE}/${drillstringId}/shares`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email } satisfies CreateShareBody),
  })
  await ensureOk(resp)
}

export async function deleteShare(drillstringId: number, userId: number): Promise<void> {
  const resp = await fetch(`${BASE}/${drillstringId}/shares/${userId}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  })
  await ensureOk(resp)
}
